import { State } from '@progress/kendo-data-query';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import useCompany from 'src/hooks/useCompany';
import useAttachDocumentApi from 'src/services/useAttachDocumentApi';
import { IAttachDocument } from 'src/types/AttachDocument';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { initialScanManagerDataState } from './ScanManagerSplitter';

interface ScanManagerContextType {
  count: number;
  showPrev: boolean;
  isInvoices: boolean;
  refreshCompany?: boolean;
  setShowPrev: SetState<boolean>;
  setRefreshCompany?: SetState<boolean>;
  getAttachDocuments: VoidFunction;
  attachmentListData: IAttachDocument[];
  selectedDocuments: IAttachDocument[];
  index: number;
  setAttachmentListData: SetState<IAttachDocument[]>;
  setIsDecrement: SetState<boolean>;
  previewDocument?: IAttachDocument;
  setDataState: SetState<State>;
  dataState: State;
  isAttachDocumentLoading: boolean;
  handleCellClick: (attachId: number) => void;
}

export const ScanManagerContext = createContext({} as ScanManagerContextType);

interface ScanManagerContextProviderProps {
  children: ReactNode;
  isInvoices: boolean;
}

const ScanManagerContextProvider: FC<ScanManagerContextProviderProps> = ({
  children,
  isInvoices,
}) => {
  const [count, setCount] = useState<number>(0);
  const { company: selectedCompany } = useCompany();
  const [refreshCompany, setRefreshCompany] = useState<boolean>(false);

  const [showPrev, setShowPrev] = useState<boolean>(true);
  const [dataState, setDataState] = useState(initialScanManagerDataState);
  const { attachDocument, isAttachDocumentLoading } = useAttachDocumentApi(isInvoices);
  const [attachmentListData, setAttachmentListData] = useState<IAttachDocument[]>([]);
  const [isDecrement, setIsDecrement] = useState<boolean>(false);

  const index = useMemo(
    () => attachmentListData.findIndex((element) => element.preview),
    [attachmentListData]
  );

  const selectedDocuments = useMemo(
    () => attachmentListData.filter((element) => element.selected),
    [attachmentListData]
  );

  const previewDocument = useMemo(
    () => attachmentListData.find((element) => element.preview),
    [attachmentListData]
  );

  const getAttachDocuments = useCallback(() => {
    const requestBody = modifyRequest(dataState, ['creationDate']);

    if (requestBody && selectedCompany?.companyId) {
      attachDocument({ companyId: selectedCompany.companyId, body: requestBody }).then((res) => {
        try {
          if (res.totalRecords > 0) {
            if (isDecrement) {
              res.items[res.items.length - 1].preview = true;
            } else {
              res.items[0].preview = true;
            }
          }
          setCount(res.totalRecords);
          setAttachmentListData(res?.items);
        } catch (error) {
          console.error('error', error);
        }
      });
    }
  }, [attachDocument, dataState, isDecrement, selectedCompany?.companyId]);

  const handleCellClick = useCallback((attachId: number) => {
    setAttachmentListData((prev) =>
      [...prev].map((listItem) => ({
        ...listItem,
        ...(attachId === listItem.attachId ? { preview: true } : { preview: false }),
      }))
    );
  }, []);

  useEffect(() => {
    getAttachDocuments();
  }, [getAttachDocuments]);

  useEffect(() => {
    setDataState(initialScanManagerDataState);
  }, [selectedCompany]);

  return (
    <ScanManagerContext.Provider
      value={{
        count,
        showPrev,
        isInvoices,
        setShowPrev,
        attachmentListData,
        getAttachDocuments,
        refreshCompany,
        setRefreshCompany,
        selectedDocuments,
        setAttachmentListData,
        index,
        previewDocument,
        setDataState,
        dataState,
        isAttachDocumentLoading,
        setIsDecrement,
        handleCellClick,
      }}
    >
      {children}
    </ScanManagerContext.Provider>
  );
};

export default ScanManagerContextProvider;
